import { AlertCircle } from 'lucide-react';
import React, { useMemo } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/catalyst/badge';
import { Switch } from '@/components/ui/catalyst/switch';
import {
  enhancedApi,
  type FirmServiceWithWorkflowsSchema,
  type FirmServiceWorkflowSchema,
  type WorkflowConfigSchema,
} from '@/services/api/generated/firmsService';
import { getApiErrorMessage } from '@/utils/api-error';

import { WorkflowRow } from './WorkflowRow';

interface WorkflowsPanelProps {
  firmId: string;
  service: FirmServiceWithWorkflowsSchema;
}

/**
 * Builds a WorkflowConfigSchema from a workflow, optionally overriding fields.
 */
const buildWorkflowConfig = (
  workflow: FirmServiceWorkflowSchema,
  overrides: Partial<Pick<WorkflowConfigSchema, 'is_enabled' | 'notes'>> = {},
): WorkflowConfigSchema => ({
  workflow_code: workflow.workflow_code,
  is_enabled: overrides.is_enabled ?? workflow.is_enabled,
  notes: overrides.notes ?? workflow.notes ?? '',
  config: workflow.config ?? undefined,
});

export const WorkflowsPanel: React.FC<WorkflowsPanelProps> = ({
  firmId,
  service,
}) => {
  const [updateWorkflows, { isLoading: isUpdating }] =
    enhancedApi.useBulkUpdateFirmServiceWorkflowsMutation();

  const [updateService, { isLoading: isUpdatingService }] =
    enhancedApi.useUpdateFirmServiceMutation();

  // Cast workflows to proper type (generated schema has loose typing due to OpenAPI limitation)
  const workflows = useMemo(
    () => (service.workflows ?? []) as FirmServiceWorkflowSchema[],
    [service.workflows],
  );

  const isServiceActive = service.is_active ?? true;
  const anyWorkflowsEnabled = workflows.some((w) => w.is_enabled);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleToggleWorkflow = async (
    workflowCode: string,
    enabled: boolean,
  ) => {
    const workflow = workflows.find((w) => w.workflow_code === workflowCode);
    if (!workflow) return;

    try {
      await updateWorkflows({
        firmId,
        firmServiceId: service.id,
        updateFirmServiceWorkflowsRequest: {
          workflows: [buildWorkflowConfig(workflow, { is_enabled: enabled })],
        },
      }).unwrap();

      toast.success(
        `Workflow ${enabled ? 'enabled' : 'disabled'} successfully`,
      );
    } catch (error) {
      toast.error(`Failed to update workflow: ${getApiErrorMessage(error)}`);
    }
  };

  const handleNotesChange = async (
    workflowCode: string,
    notes: string,
  ): Promise<void> => {
    const workflow = workflows.find((w) => w.workflow_code === workflowCode);
    if (!workflow) return;

    await updateWorkflows({
      firmId,
      firmServiceId: service.id,
      updateFirmServiceWorkflowsRequest: {
        workflows: [buildWorkflowConfig(workflow, { notes })],
      },
    }).unwrap();
  };

  const handleBulkUpdate = async (enabled: boolean) => {
    const workflowConfigs = workflows.map((w) =>
      buildWorkflowConfig(w, { is_enabled: enabled }),
    );

    try {
      await updateWorkflows({
        firmId,
        firmServiceId: service.id,
        updateFirmServiceWorkflowsRequest: { workflows: workflowConfigs },
      }).unwrap();

      toast.success(`All workflows ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error(
        `Failed to ${enabled ? 'enable' : 'disable'} workflows: ${getApiErrorMessage(error)}`,
      );
    }
  };

  const handleToggleService = async (active: boolean) => {
    if (!active && anyWorkflowsEnabled) {
      toast.error(
        'Please disable all workflows before turning off the service',
      );
      return;
    }

    try {
      await updateService({
        firmId,
        id: service.id,
        patchedUpdateFirmServiceSchema: { is_active: active },
      }).unwrap();

      toast.success(
        `Service ${active ? 'activated' : 'deactivated'} successfully`,
      );
    } catch (error) {
      toast.error(`Failed to toggle service: ${getApiErrorMessage(error)}`);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-4">
      {/* Header with service name and toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-zinc-950 dark:text-white">
            {service.service.display_name}
          </h2>
          <Badge color={isServiceActive ? 'blue' : 'zinc'}>
            {isServiceActive ? 'On' : 'Off'}
          </Badge>
        </div>

        <Switch
          checked={isServiceActive}
          onChange={handleToggleService}
          disabled={
            isUpdatingService || (isServiceActive && anyWorkflowsEnabled)
          }
          color="blue"
          aria-label={`Toggle ${service.service.display_name} service`}
        />
      </div>

      {/* Notice banner for inactive service */}
      {!isServiceActive && workflows.length > 0 && (
        <div className="flex items-start gap-2 px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg dark:bg-zinc-900 dark:border-zinc-700">
          <AlertCircle className="h-4 w-4 text-zinc-400 dark:text-zinc-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-zinc-600 dark:text-zinc-400">
            Service is off. Workflows and configuration are read-only.
          </p>
        </div>
      )}

      {/* Workflows card */}
      <div className="border border-zinc-200 rounded-lg bg-white dark:border-zinc-800 dark:bg-zinc-950">
        <WorkflowsCardHeader
          hasWorkflows={workflows.length > 0}
          isUpdating={isUpdating}
          isServiceActive={isServiceActive}
          onEnableAll={() => handleBulkUpdate(true)}
          onDisableAll={() => handleBulkUpdate(false)}
        />

        <div className="divide-y divide-zinc-200 dark:divide-zinc-800">
          {workflows.length === 0 ? (
            <div className="px-3 py-8 text-center text-sm text-zinc-500 dark:text-zinc-400">
              No workflows configured
            </div>
          ) : (
            workflows.map((workflow) => (
              <WorkflowRow
                key={workflow.workflow_code}
                firmId={firmId}
                serviceId={service.id}
                workflow={workflow}
                disabled={!isServiceActive}
                onToggle={(enabled) =>
                  handleToggleWorkflow(workflow.workflow_code, enabled)
                }
                onNotesChange={(notes) =>
                  handleNotesChange(workflow.workflow_code, notes)
                }
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Sub-component: WorkflowsCardHeader
// -----------------------------------------------------------------------------

interface WorkflowsCardHeaderProps {
  hasWorkflows: boolean;
  isUpdating: boolean;
  isServiceActive: boolean;
  onEnableAll: () => void;
  onDisableAll: () => void;
}

const WorkflowsCardHeader: React.FC<WorkflowsCardHeaderProps> = ({
  hasWorkflows,
  isUpdating,
  isServiceActive,
  onEnableAll,
  onDisableAll,
}) => (
  <div className="px-3 py-2 border-b border-zinc-200 dark:border-zinc-800">
    <div className="flex items-center justify-between">
      <h3 className="text-sm font-medium text-zinc-950 dark:text-white">
        Workflows
      </h3>
      {hasWorkflows && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onEnableAll}
            disabled={isUpdating || !isServiceActive}
            className="text-xs font-medium text-zinc-700 hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUpdating ? 'Enabling...' : 'Enable all'}
          </button>
          <button
            type="button"
            onClick={onDisableAll}
            disabled={isUpdating || !isServiceActive}
            className="text-xs font-medium text-zinc-700 hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUpdating ? 'Disabling...' : 'Disable all'}
          </button>
        </div>
      )}
    </div>
  </div>
);
